import { useCallback, useMemo, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { Elements } from '@stripe/react-stripe-js'

// @mui material components
import Grid from '@mui/material/Grid'

// Material Dashboard 2 React components
import MDBox from '../../components/MDComponents/MDBox'

// Material Dashboard 2 React example components
import DashboardLayout from '../../layouts/LayoutContainers/DashboardLayout'
import PaymentModal from './components/PaymentModal'
import PaymentCard from './components/PaymentCard'
import Spinner from '../../components/Spinner'
import usePayment from './usePayment'
import SkeletonCard from './components/SkeletonCard'
import Button from '../../components/Button'
import Modal from '../../components/Modal'

import { settings } from '../../settings'
import { monthNames, titleCase } from '../../utils'

const Payment = () => {
  const {
    stripePromise,
    handleGetStripeOptions,
    handleCreateSubscription,
    handleCloseModal,
    handleLoadSubscriptions,
    handleCancelSubscription,
    handleContinueSubscription,
    onPaymentSuccess,
    isNotLarge,
    paymentCardsWidth,
    handleChange,
    handleShowVoucher,
    subscriptionsInfo,
    prices,
    subscription,
    openingModal,
    isLoading,
    voucher,
  } = usePayment()

  const isLargeScreen = useMediaQuery({
    query: '(min-width: 1800px)',
  })

  const getEndDateString = useCallback(end_timestamp => {
    return (
      settings.exam_type + ' ' + new Date(end_timestamp * 1000).getFullYear()
    )
  }, [])

  const getCardName = useCallback(
    price => {
      if (price === undefined) {
        return 'Special'
      }

      if (price['interval_count'] === 1) {
        return 'O lună'
      }

      if (price['interval_count'] === 3) {
        return 'Trei luni'
      }

      if (price['interval_count'] === 6) {
        return '6 luni'
      }

      if (price['interval_count'] === undefined) {
        return titleCase(getEndDateString(price['end_timestamp']))
      }
    },
    [getEndDateString],
  )

  const getCardDescription = useCallback(price => {
    if (price === undefined) {
      return 'Tip de abonament creat special'
    }

    if (price['interval_count'] === 1) {
      return 'Acces complet, lună de lună'
    }

    if (price['interval_count'] === 3) {
      return 'Trimestrial, pentru cei hotărâți'
    }

    if (price['interval_count'] === 6) {
      return 'Pentru cei hotărâți'
    }

    if (price['interval_count'] === undefined) {
      const dateEnd = new Date(price['end_timestamp'] * 1000 + 14400000)
      const year = dateEnd.getFullYear()
      const month = dateEnd.getMonth()
      const day = dateEnd.getDate()
      return 'Până pe ' + day + ' ' + monthNames[month] + ' ' + year
    }
  }, [])

  const hasOneTimeSubscription = useCallback(() => {
    const oneTimePriceId = prices?.prices[2].price_id

    return subscriptionsInfo && subscriptionsInfo[oneTimePriceId]
      ? subscriptionsInfo[oneTimePriceId].active
      : false
  }, [prices, subscriptionsInfo])

  const cards = useMemo(
    () =>
      prices?.prices.map(price => ({
        name: getCardName(price),
        description: getCardDescription(price),
        price: price.price_value,
        oldPrice: undefined,
        priceId: price.price_id,
        oneTime: !('interval_count' in price),
        interval:
          'interval_count' in price
            ? price['interval_count']
            : (price['end_timestamp'] - Date.now() / 1000) / 86400 / 30,
      })),
    [getCardDescription, getCardName, prices],
  )

  const [isModal, setIsModal] = useState(false)

  const subscriptionBenefits = [
    `Peste ${settings.grile_count} de grile`,
    'Număr nelimitat de simulări',
    'Ordine personalizată a grilelor',
    'Explicații',
    'Indicii',
    'Statistici',
  ]

  return (
    <DashboardLayout>
      <Elements stripe={stripePromise} options={handleGetStripeOptions()}>
        <PaymentModal
          subscription={subscription}
          handleCloseModal={handleCloseModal}
          onPaymentSuccess={onPaymentSuccess}
        />
      </Elements>

      <MDBox
        className={`flex flex-col justify-between items-center justify-items-center  ${
          isLargeScreen ? 'mt-[20vh]' : 'mt-[10vh]'
        }`}
      >
        {!openingModal ? (
          <>
            <Grid
              container
              alignItems="center"
              className={`flex justify-center items-end !w-[full] !max-w-[1600px]`}
            >
              {!cards || isLoading ? (
                <>
                  {[0, 1, 2].map((card, index) => {
                    return (
                      <Grid
                        key={`skeleton-card-${index}`}
                        item
                        className="!m-[30px] !w-[fit-content]"
                      >
                        <SkeletonCard />
                      </Grid>
                    )
                  })}
                </>
              ) : (
                <>
                  {cards.map((card, index) => {
                    return (
                      <Grid
                        key={`payment-card-${index}`}
                        item
                        className="!m-[30px] !w-[fit-content]"
                      >
                        <PaymentCard
                          name={card.name}
                          description={card.description}
                          price={card.price}
                          oldPrice={card.oldPrice}
                          price_id={card.priceId}
                          createSubscription={handleCreateSubscription}
                          continueSubscription={handleContinueSubscription}
                          cancelSubscription={handleCancelSubscription}
                          subscriptionsInfo={subscriptionsInfo}
                          disabled={hasOneTimeSubscription()}
                          oneTime={card.oneTime}
                          interval={card.interval}
                        />
                      </Grid>
                    )
                  })}
                </>
              )}
            </Grid>

            <Modal
              maxHeight="80vh"
              width={600}
              title={'Toate abonamentele includ următoarele beneficii:'}
              isOpen={isModal}
              closeModal={() => {
                setIsModal(false)
              }}
            >
              <div className="w-full h-full flex flex-col justify-start items-start py-8">
                {subscriptionBenefits.map(benefit => {
                  return (
                    <div
                      key={benefit}
                      className="flex flex-row items-center my-[5px]"
                    >
                      <img
                        src="https://grile.info/home/images/check.webp"
                        className="bg-primary w-[25px] h-[25px] rounded-full p-1"
                      />
                      <p className="text-lg ml-1 leading-5">{benefit}</p>
                    </div>
                  )
                })}
              </div>
            </Modal>

            <p className="text-sm text-center text-over-primary-background opacity-80 block">
              *Prețul rămâne același la fiecare reînnoire până la anularea
              abonamentului, indiferent de modificările ulterioare
            </p>
            <p className="text-sm text-center text-over-primary-background opacity-80 inline-block">
              Vezi{' '}
              <div
                onClick={() => {
                  setIsModal(true)
                }}
                className="text-primary underline cursor-pointer inline-block"
              >
                aici
              </div>{' '}
              ce beneficii conține fiecare abonament
            </p>
            {handleShowVoucher() && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'center',
                  marginTop: '50px',
                  gap: '10px',
                }}
              >
                <div className="font-bold text-over-primary-background">
                  Ai un cod de voucher?
                </div>
                <form>
                  <label>
                    <input
                      type="text"
                      name="voucher"
                      className="rounded-[4px] text-over-secondary-background bg-container-background !p-2 min-h-8 border  border-primary text-sm"
                      placeholder="Introdu codul..."
                      onChange={handleChange}
                      value={voucher}
                    />
                  </label>
                </form>
                <MDBox className="mx-auto">
                  <Button
                    className="bg-primary w-20 h-10"
                    disabled={!voucher}
                    onClick={handleLoadSubscriptions}
                  >
                    Aplică
                  </Button>
                </MDBox>
              </div>
            )}
          </>
        ) : (
          <Spinner />
        )}
      </MDBox>
    </DashboardLayout>
  )
}

export default Payment
